import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useWorkflowView } from '../contexts/WorkflowViewContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ThemeToggle from './ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import NotificationCenter from './NotificationCenter';
import RecentActivityPanel from './RecentActivityWidget';
import SparkleIcon from './icons/SparkleIcon';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
const LIBRARY_PATHS = ['/prompt-templates', '/knowledge-base', '/insights'];

const ChevronIcon: React.FC<{ open: boolean }> = ({ open }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" className={`transition-transform ${open ? 'rotate-180' : ''}`}>
    <path d="M7,10L12,15L17,10H7Z" />
  </svg>
);

interface HeaderMenuItem {
  to: string;
  label: string;
  badge?: number;
}

/** Small click-to-open dropdown used for the Library and Arie menus. */
const HeaderMenu: React.FC<{
  trigger: (open: boolean) => React.ReactNode;
  items: HeaderMenuItem[];
  align: 'left' | 'right';
  /** data-tour anchor for the product tour. */
  tourId?: string;
}> = ({ trigger, items, align, tourId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative" data-tour={tourId}>
      <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open}>
        {trigger(open)}
      </button>
      {open && (
        <div
          role="menu"
          className={`absolute ${align === 'right' ? 'right-0' : 'left-0'} mt-2 w-52 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-50 p-1`}
        >
          {items.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              role="menuitem"
              className="flex items-center justify-between px-3 py-2 text-sm text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-border rounded transition-colors"
            >
              {item.label}
              {!!item.badge && (
                <span className="bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

const Header: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const { view: workflowView, toggle: viewToggle, setView: setWorkflowView } = useWorkflowView();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showActivityPanel, setShowActivityPanel] = useState(false);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [agentApprovals, setAgentApprovals] = useState(0);

  // Close the mobile drawer whenever we change route
  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const fetchUnreadTickets = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/support-tickets/unread-count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setUnreadTickets(data.count);
      }
    } catch {
      // Silently fail
    }
  }, [token]);

  useEffect(() => {
    fetchUnreadTickets();
    const interval = setInterval(fetchUnreadTickets, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadTickets, location.pathname]);

  // Agent Mailbox: pending approvals badge on the Agent nav link.
  const fetchAgentApprovals = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE_URL}/api/agent/notifications`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setAgentApprovals(Number(data.pendingApprovals) || 0);
      }
    } catch {
      // Silently fail
    }
  }, [token]);

  useEffect(() => {
    fetchAgentApprovals();
    const interval = setInterval(fetchAgentApprovals, 60000);
    return () => clearInterval(interval);
  }, [fetchAgentApprovals, location.pathname]);

  // Safely derive initials, fallback to empty
  const initials = user
    ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}`
    : '';

  const isActive = (path: string) => {
    return location.pathname === path ? 'text-primary' : 'text-secondary dark:text-dark-text';
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
    navigate('/login');
  };

  return (
    <>
      <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-b-[#e7eef3] dark:border-dark-border bg-white dark:bg-dark-bg px-4 md:px-10 py-3 transition-colors duration-200 print:hidden">
        <button 
          className="flex items-center gap-4 text-secondary dark:text-dark-text hover:text-primary transition-colors"
          onClick={() => {
            navigate('/');
          }}
        >
          <div className="size-4">
            <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M24 4L44 12v10c0 12-8 24-20 28C12 46 4 34 4 22V12l20-8z"
                fill="currentColor"
              />
              <path
                d="M20 18l4 4 8-8"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h2 className="text-secondary dark:text-dark-text text-lg font-bold leading-tight tracking-[-0.015em]">Risk Ninja</h2>
        </button>
        <div className="flex flex-1 justify-end gap-4 md:gap-8">
          <div className="hidden md:flex items-center gap-9">
            {user?.consumesLicense !== false && (
              <>
                <button
                  className={`text-sm font-medium leading-normal hover:text-primary transition-colors ${isActive('/')}`}
                  onClick={() => {
                    navigate('/');
                  }}
                >
                  Home
                </button>
                <Link className={`relative text-sm font-medium leading-normal hover:text-primary transition-colors ${isActive('/support')}`} to="/support">
                  Support
                  {unreadTickets > 0 && (
                    <span className="absolute -top-2 -right-4 bg-red-500 text-white text-xs font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                      {unreadTickets}
                    </span>
                  )}
                </Link>
                <HeaderMenu
                  align="left"
                  tourId="library-menu"
                  trigger={(open) => (
                    <span className={`flex items-center gap-0.5 text-sm font-medium leading-normal hover:text-primary transition-colors ${LIBRARY_PATHS.includes(location.pathname) ? 'text-primary' : 'text-secondary dark:text-dark-text'}`}>
                      Library
                      <ChevronIcon open={open} />
                    </span>
                  )}
                  items={[
                    { to: '/prompt-templates', label: 'Templates' },
                    { to: '/knowledge-base', label: 'Knowledge Base' },
                    { to: '/insights', label: 'Insights' },
                  ]}
                />
              </>
            )}
            {(user?.role === 'admin' || user?.role === 'system_admin') && (
              <Link
                className={`text-sm font-medium leading-normal hover:text-primary transition-colors ${isActive('/admin')}`}
                to="/admin"
              >
                Admin
              </Link>
            )}
            {user?.role === 'system_admin' && (
              <Link
                className={`text-sm font-medium leading-normal hover:text-primary transition-colors ${isActive('/sysadmin')}`}
                to="/sysadmin"
              >
                SysAdmin
              </Link>
            )}
          </div>
          <div className="flex items-center gap-3">
            {/* Recent activity drawer trigger */}
            {user && (
              <button
                data-tour="recent-activity"
                onClick={() => setShowActivityPanel(true)}
                aria-label="Open recent activity"
                title="Recent activity"
                className="p-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="12" r="9" strokeWidth={2} />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 7v5l3 2" />
                </svg>
              </button>
            )}

            {/* Arie agent menu — chat lives in the floating ninja head */}
            {user && user.consumesLicense !== false && (
              <HeaderMenu
                align="right"
                tourId="arie-menu"
                trigger={(open) => (
                  <span
                    title={agentApprovals > 0 ? `${agentApprovals} waiting for your approval` : 'Arie agent'}
                    className={`relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      location.pathname.startsWith('/agent') ? 'text-primary' : 'text-gray-500 dark:text-gray-400 hover:text-primary hover:bg-gray-100 dark:hover:bg-dark-surface'
                    }`}
                  >
                    <SparkleIcon className="w-4 h-4" />
                    <span className="hidden md:inline">Arie</span>
                    <ChevronIcon open={open} />
                    {agentApprovals > 0 && (
                      <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs font-bold rounded-full h-4 min-w-[16px] flex items-center justify-center px-1">
                        {agentApprovals}
                      </span>
                    )}
                  </span>
                )}
                items={[
                  { to: '/agent', label: 'Agent activity' },
                  { to: '/agent?tab=approvals', label: 'Approvals', badge: agentApprovals },
                  { to: '/agent?tab=inbox', label: 'Agent inbox' },
                  { to: '/agent?tab=settings', label: 'Agent settings' },
                  { to: '/settings', label: 'Forwarding addresses' },
                ]}
              />
            )}

            {/* Notification Center - Bell icon for workflow jobs */}
            <div data-tour="notifications">
              <NotificationCenter />
            </div>

            <ThemeToggle />

            {/* Hamburger - mobile only */}
            {user && (
              <button
                type="button"
                onClick={() => setShowMobileMenu((v) => !v)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface text-secondary dark:text-dark-text"
                aria-label="Toggle navigation menu"
                aria-expanded={showMobileMenu}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {showMobileMenu ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            )}

            {user && viewToggle && workflowView === 'dev' && (
              <span
                title="You're seeing the full development catalogue. Switch back to Prod in your user menu to see what production users see."
                className="hidden md:inline-block px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
              >
                Dev view
              </span>
            )}
            {user ? (
              <div className="relative hidden md:block" data-tour="user-menu">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors"
                >
                  <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center text-sm font-medium">
                    {initials}
                  </div>
                  <span className="text-sm font-medium text-secondary dark:text-dark-text">
                    {user.firstName}
                  </span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-accent dark:text-dark-muted">
                    <path d="M7,10L12,15L17,10H7Z" />
                  </svg>
                </button>
                
                {showUserMenu && (
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg shadow-lg z-50">
                    <div className="p-3 border-b border-gray-200 dark:border-dark-border">
                      <div className="text-sm font-medium text-secondary dark:text-dark-text">
                        {user.firstName} {user.lastName}
                      </div>
                      <div className="text-xs text-accent dark:text-dark-muted">
                        {user.email}
                      </div>
                    </div>
                    <div className="p-1">
                      <Link
                        to="/settings"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full text-left px-3 py-2 text-sm text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-border rounded transition-colors block"
                      >
                        Settings
                      </Link>
                      <Link
                        to="/change-password"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full text-left px-3 py-2 text-sm text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-border rounded transition-colors block"
                      >
                        Change Password
                      </Link>
                      {viewToggle && (
                        <div className="px-3 py-2">
                          <div className="text-xs text-accent dark:text-dark-muted mb-1">Workflow view</div>
                          <div className="flex rounded-md border border-gray-200 dark:border-dark-border overflow-hidden text-xs">
                            {(['prod', 'dev'] as const).map((v) => (
                              <button
                                key={v}
                                type="button"
                                onClick={() => setWorkflowView(v).catch(() => undefined)}
                                className={`flex-1 px-2 py-1 transition-colors ${
                                  workflowView === v
                                    ? 'bg-primary text-white'
                                    : 'text-secondary dark:text-dark-text hover:bg-gray-50 dark:hover:bg-dark-border'
                                }`}
                              >
                                {v === 'prod' ? 'Prod' : 'Dev'}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className="px-4 py-2 bg-primary hover:bg-blue-600 text-white rounded-lg font-medium transition-colors"
              >
                Sign In
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      {user && showMobileMenu && (
        <div className="md:hidden border-b border-gray-200 dark:border-dark-border bg-white dark:bg-dark-bg shadow-sm print:hidden">
          <nav className="px-4 py-3 flex flex-col gap-1">
            {user?.consumesLicense !== false && (
              <>
                <button
                  className={`text-left px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors ${isActive('/')}`}
                  onClick={() => {
                    navigate('/');
                    setShowMobileMenu(false);
                  }}
                >
                  Home
                </button>
                <Link
                  to="/support"
                  onClick={() => setShowMobileMenu(false)}
                  className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors flex items-center justify-between ${isActive('/support')}`}
                >
                  <span>Support</span>
                  {unreadTickets > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
                      {unreadTickets}
                    </span>
                  )}
                </Link>
                <Link
                  to="/prompt-templates"
                  onClick={() => setShowMobileMenu(false)}
                  className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors ${isActive('/prompt-templates')}`}
                >
                  Templates
                </Link>
                <Link
                  to="/knowledge-base"
                  onClick={() => setShowMobileMenu(false)}
                  className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors ${isActive('/knowledge-base')}`}
                >
                  Knowledge Base
                </Link>
                <Link
                  to="/insights"
                  onClick={() => setShowMobileMenu(false)}
                  className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors ${isActive('/insights')}`}
                >
                  Insights
                </Link>
                <Link
                  to="/agent"
                  onClick={() => setShowMobileMenu(false)}
                  className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors flex items-center justify-between ${isActive('/agent')}`}
                >
                  Agent
                  {agentApprovals > 0 && (
                    <span className="bg-amber-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1.5">
                      {agentApprovals}
                    </span>
                  )}
                </Link>
              </>
            )}
            {(user?.role === 'admin' || user?.role === 'system_admin') && (
              <Link
                to="/admin"
                onClick={() => setShowMobileMenu(false)}
                className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors ${isActive('/admin')}`}
              >
                Admin
              </Link>
            )}
            {user?.role === 'system_admin' && (
              <Link
                to="/sysadmin"
                onClick={() => setShowMobileMenu(false)}
                className={`px-3 py-2 rounded-lg text-base font-medium hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors ${isActive('/sysadmin')}`}
              >
                SysAdmin
              </Link>
            )}

            <div className="mt-2 pt-2 border-t border-gray-200 dark:border-dark-border">
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                Signed in as
                <div className="font-medium text-secondary dark:text-dark-text">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs">{user.email}</div>
              </div>
              <Link
                to="/settings"
                onClick={() => setShowMobileMenu(false)}
                className="block px-3 py-2 rounded-lg text-base font-medium text-secondary dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors"
              >
                Settings
              </Link>
              <Link
                to="/change-password"
                onClick={() => setShowMobileMenu(false)}
                className="block px-3 py-2 rounded-lg text-base font-medium text-secondary dark:text-dark-text hover:bg-gray-100 dark:hover:bg-dark-surface transition-colors"
              >
                Change Password
              </Link>
              <button
                onClick={() => {
                  setShowMobileMenu(false);
                  handleLogout();
                }}
                className="w-full text-left px-3 py-2 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              >
                Sign Out
              </button>
            </div>
          </nav>
        </div>
      )}

      {/* Click outside to close user menu */}
      {showUserMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowUserMenu(false)}
        />
      )}

      {/* Recent Activity drawer (right side, hidden by default) */}
      {user && (
        <RecentActivityPanel
          isOpen={showActivityPanel}
          onClose={() => setShowActivityPanel(false)}
        />
      )}
    </>
  );
};

export default Header; 